import { useMemo, useState } from "react"

/**
 * @param {string} text 
 */
export default function useChatSuggestions(text = "") {
  const [maxSuggestionLength] = useState(() => Math.max(...SUGGESTIONS.map(item => item.length)))
  const matchedSuggestions = useMemo(() => {
    if(text.length < 3) return null
    if(text.length > maxSuggestionLength) return null
    
    const processedText = escapeRegExp(text.toLowerCase())
    const regExp = new RegExp(`^(.*)(${processedText})(.*)$`)

    return SUGGESTIONS.map((str, i) => {
      const match = str.toLowerCase().match(regExp)
      if(
        text.trim() === "" ||
        match == null
      ) return null

      const [_, start, mstr, end] = match
      const matchIndexes = [0]
      matchIndexes.push(start.length)
      matchIndexes.push(start.length + mstr.length)

      return ({
        node: () => (
          <>
            <b>{str.slice(matchIndexes[0], matchIndexes[1])}</b>
            <span>{str.slice(matchIndexes[1], matchIndexes[2])}</span>
            <b>{str.slice(matchIndexes[2])}</b>
          </>
        ),
        value: str,
        start: matchIndexes[1]
      })
    })
    .sort((a, b) => (a?.start ?? 0) - (b?.start ?? 0))
    .filter(item => item != null).slice(0, 5)
  }, [text, maxSuggestionLength])

  const suggestionsHash = useMemo(() => (matchedSuggestions ?? []).reduce((acc, suggestion) => (
    acc += SUGGESTIONS.findIndex(item => item === suggestion.value)
  ), 0), [ matchedSuggestions ])

  return {
    suggestions: matchedSuggestions ?? [],
    suggestionsHash
  }
}

/**
 * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
 * @param {string} text 
 */
function escapeRegExp(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const SUGGESTIONS = [...new Set([
  "What is wrong with me?",
  "What is wrong with the patient?",
  "What do you see?",
  "Write a medical report for the image",
  "Generate a medical report for the image",
  "How long does the flu last?",
  "How to lower cholesterol?",
  "How to stop snoring?",
  "Is strep throat contagious?",
  "Please explain my test results",
  "What causes warts?",
  "Does cholesterol in food count?",
  "Does the medicine have side effects?",
  "How effective is this treatment?",
  "How were vaccines invented?",
  "Is arthritis avoidable?",
  "Is bronchitis contagious?",
  "Is tap water safe to drink?",
  "Should I take a multivitamin?",
  "What are allergies?",
  "Why do I feel nauseous?",
  "How do you get monkeypox?",
  "Why do I sweat in my sleep?",
  "How to get rid of allergies?",
  "How many calories should I eat a day?",
  "What is considered a fever?",
  "What does monkeypox look like?",
  "How many hours of sleep do you need?",
  "How to relieve bloating?",
  "How long do allergies last?",
  "Can allergies cause a cough?",
  "What is gout?",
  "What causes hemorrhoids?",
  "What causes diabetes?",
  "How to treat ear infection?",
  "Are sinus infections contagious?",
  "How to stop a nosebleed?",
  "How many hours of sleep should you get?",
  "What is a juice cleanse?",
  "Can you live without a kidney?",
  "Why do I keep yawning?",
  "How to stop vertigo?",
  "How to stop night sweats?",
  "Why do I pee when I sneeze?",
  "Is swallowing gum bad for you?",
  "How to remove an ingrown toenail?",
  "Is it bad to sleep with your bra on?",
  "How to relieve lower back pain?",
  "How to stop mosquito bites from itching?",
  "How much fiber do I need?",
  "What does monkey pox look like?",
  "How long does pink eye last?",
  "How to prevent a stroke?",
  "How to get rid of hiccups?",
  "How much water should I drink a day?",
  "How many bones are in the human body?",
  "What is anemia?",
  "How long does strep throat last?",
  "How to break a fever?",
  "What does heartburn feel like?",
  "Is poison ivy contagious?",
  "What happens when you eat mold?",
  "How to relieve tension headaches?",
  "How to relieve sinus headache?",
  "How to prevent arthritis?",
  "How do I know if I have pneumonia?",
  "How much melatonin is too much?",
  "Why do I cough at night?",
  "How to treat ingrown toenail?",
  "How to stop acid reflux?",
  "How to stop tinnitus?",
  "How to relieve sciatica pain?",
  "What causes vertigo?",
  "How to get rid of a sore throat?",
  "How to get rid of a headache?",
  "Why do I have a headache?",
  "Is melatonin safe?",
  "What causes low blood pressure?",
  "How do you get pink eyes?",
  "How to pop your ears?",
  "How much caffeine is too much?",
  "Can you live without a spleen?",
  "Why do I drool when I sleep?",
  "What causes restless legs?",
  "What kills toenail fungus?",
  "How to prevent heart disease?",
  "What is a normal blood pressure?",
  "What is a bunion?",
  "Why am I always hungry?",
  "How to relieve gas pain?",
  "How to get rid of a cold sore in 24 hours?",
  "What is a pap smear?",
  "What causes migraines?",
  "How to stop bug bites from itching?",
  "How to stop coughing?",
  "How long does a cold last?",
  "What is a normal heart rate?",
  "How to remove a tick?",
  "How to get rid of canker sores?",
  "How long does stomach flu last?",
  "What does ringworm look like?",
  "Why does coffee make you poop?",
  "Can you live without a spine?",
  "How to prevent diabetes?",
  "How to prevent blood clots?",
  "What causes allergies?",
  "Can allergies make you cough?",
  "How to treat eczema?",
  "How to relieve sinus pressure?",
  "What happens when you swallow gum?",
  "What causes skin tags?",
  "What causes sleep paralysis?",
  "Why are my eyes red?",
  "Can allergies cause headaches?",
  "Is it bad to crack your back?",
  "What causes high cholesterol?",
  "How to prevent shin splints?",
  "How to prevent pneumonia?",
  "How much protein do I need?",
  "How to stop leg cramps?",
  "What causes psoriasis?",
  "Is Ozempic safe?",
  "What does shingles look like?",
  "What does pink eye look like?",
  "How to relieve ear pain?",
  "How to get rid of stye?",
  "What are hemorrhoids?",
  "How to get rid of heartburn?",
  "What does a spider bite look like?",
  "How long can you go without sleep?",
  "Can you live without a liver?",
  "How to get rid of bloating?",
  "Why does my stomach hurt after I eat?",
  "What causes muscle spasms?",
  "What does a tick bite look like?",
  "How to relieve ear pressure?",
  "Is pneumonia contagious?",
  "What causes canker sores?",
  "What does skin cancer look like?",
  "What does strep throat look like?",
  "What is a good resting heart rate?",
  "What causes kidney stones?",
  "What is hypertension?",
  "How to get rid of stuffy nose?",
  "Is diabetes genetic?",
  "What kills stomach virus?",
  "How to prevent dementia?",
  "How long can you go without food?",
  "What causes high blood pressure?",
  "What causes heartburn?",
  "Are ear infections contagious?",
  "Can allergies cause a sore throat?",
  "Why are my hands so dry?",
  "How to treat food poisoning?",
  "Why are my gums swollen?",
  "Are allergies genetic?",
  "Can you get allergies in the winter?",
  "What happens if you drink too much water?",
  "Why do I feel lightheaded?",
  "What causes excessive gas?",
  "Can you die from a broken heart?",
  "How to stop a panic attack?",
  "How to prevent kidney stones?",
  "Can allergies cause swollen lymph nodes?",
  "How much coffee is too much?",
  "What is the rarest blood type?",
  "What causes eczema?",
  "How to get rid of tonsil stones?",
  "Why are my feet swollen?",
  "How to stop ear ringing?",
  "Is breast cancer genetic?",
  "How often should I workout?",
  "Why do I always feel tired?",
  "Is rainwater safe to drink?",
  "How to relieve period cramps?",
  "What kills toenail fungus instantly?",
  "What causes hiccups?",
  "How to get water out of your ear?",
  "What causes seizures?",
  "What causes a stroke?",
  "Why do I bruise so easily?",
  "What is lupus?",
  "How long does food poisoning last?",
  "What side is your appendix on?",
  "What is vertigo?",
  "Why does my stomach hurt?",
  "What causes tonsil stones?",
  "Why does my throat hurt when I swallow?",
  "How to treat strep throat?",
  "Why are my ankles swollen?",
  "Why do I pee when I cough?",
  "What do blood clots look like?",
  "Why do I keep getting hiccups?",
  "How to relieve neck pain?",
  "Why do I keep sneezing?",
  "Why do I hear ringing in my ear?",
  "How long should you shower for?",
  "How to get rid of dandruff?",
  "Why do I sweat so much?",
  "How to treat a burn?",
  "What is pneumonia?",
  "Can allergies make you tired?",
  "Is it bad to crack your neck?",
  "What causes sleep apnea?",
  "How to treat a sinus infection?",
  "Is ringworm contagious?",
  "What is a hernia?",
  "How is monkeypox transmitted?",
  "What causes dizziness?",
  "What is sleep apnea?",
  "What do bed bug bites look like?",
  "Why is my eye twitching?",
  "How to get rid of nausea?",
  "Can allergies cause a fever?",
  "How to remove ear wax?",
  "What is Ozempic?",
  "What happens if you eat mold?",
  "What temperature is a fever?",
  "How long is flu contagious?",
  "Should I Go Gluten-Free?",
  "Is a Daily Glass of Wine Healthy?",
  "Are Short Workouts Worth It?",
  "Is Tap Water Safe to Drink?",
  "Sugar or High Fructose Corn Syrup?",
  "Does Cholesterol in Food Count?",
  "Do Vaccines Cause Autism Spectrum Disorder?",
  "Is Microwaved Food Unsafe?",
  "Can I Be Fat and Healthy?",
  "How should I care for my stitches?",
  "Can I get my stitches wet in the bath or shower?",
  "How do I care for a wound treated with skin glue?",
  "How long will my stitches (sutures) take to dissolve?",
  "When do I need a fit note?",
  "Can I get a fit note without seeing a healthcare professional?",
  "Can I go to work before the end date on my fit note?",
  "Can I pick a prescription for someone else?",
  "How should I collect and store a poo (stool) sample?",
  "How should I check my breasts?",
  "How long is a prescription valid for?",
  "What should my testicles look and feel like?",
  "Why should I avoid sexual activity before a PSA?",
  "Is it better to have a bath or shower after surgery?",
  "How soon can I go swimming after surgery?",
  "What causes bleeding between periods?",
  "What causes a woman to bleed after sex?",
  "Well I'm in my 50s, but honestly, I feel like I'm still young. At what age should I get serious about my health?",
  "Wait — I heard doctors don't worry so much about high cholesterol anymore. Is that true?",
  "Does all this 'healthy living' stuff really help me live longer?",
  "Is it okay to wash my hair everyday?",
  "What's the best diet? Is it keto? Paleo? G-free? Do I need to live on acai and buttered coffee?",
  "I've heard that keeping calories down is linked to longevity. Does that mean I should be fasting?",
  "I know what I'm supposed to eat, but how can I keep eating what I enjoy?",
  "Skipping breakfast is the new diet trend. Good for me?",
  "How can I eat a healthy diet when I'm on a budget?",
  "I have, like, a billion different vitamins in my cabinet. Isn't that enough?",
  "You mean vitamin pills are useless?",
  "What about my fish oil pills? I read they may not prevent heart attacks, after all. True?",
  "Is there a secret to making sure I get great medical care?",
  "More healthy foods, a good doctor and watch the pills. What's another lifestyle change I can make today that will help the most?",
  "Is that all I need for exercise — a long walk?",
  "I hate gyms, loud music and sweaty people. What else can I do to preserve mobility?",
  "Hmm, still a little sweaty. What's a 100 percent sweat-free way to improve my health?",
  "What's a worthwhile fitness goal for people in their 50s?",
  "I've been exercising but haven't lost any weight. It feels pointless.",
  "My joints ache after exercise. Is arthritis setting in?",
  "Are fitness watches worth the expense?I've had back pain for years. Do I have to learn to live with it?",
  "What else can I do for my bad back?",
  "What's a 'healthy' sex drive for someone in their 50s?",
  "I'm just not enjoying growing older. What's wrong with me?",
  "What can I do about the stress in my life?",
  "I try to 'stay young' in how I live and think. Am I being ridiculous?",
  "I'm thinking about doing some surgical 'freshening up,' but is cosmetic surgery safe?",
  "My husband refuses to talk about his health or go to the doctor. Is he hiding something?",
  "How do I shake this chronic exhaustion I've picked up during the pandemic?",
  "I had COVID last winter, and I still feel off. Is this long COVID?",
  "I don't feel as sharp at work as I used to. What can I do?",
  "How do I prevent myself from becoming a caregiving nightmare to my kids?",
  "A loved one and I are estranged because our values just don't align. How do I reconnect?",
  "I have hypertension. Are blood pressure cuffs worth the money?",
  "What's the first sign of heart disease?",
  "I love my nightly glass of wine. A little bit of vino is good for my heart, right?",
  "How about marijuana? Is it safe?",
  "I have the occasional cigarette. Does that really matter?",
  "Does 'prediabetes' mean you're definitely going to get diabetes?",
  "What's a simple, tech-free way to measure my health?",
  "How much should I worry about cancer in my 50s?",
  "So, if cancer runs in my family, should I get screened regularly?",
  "What about prostate cancer? Should I get a PSA test?",
  "A direct relative was legally blind when she died. How do I protect my eyes?",
  "Speaking of senses, what can I do about my damn tinnitus?",
  "What's the number one thing I can do to help prevent dementia?",
  "Yikes! I'll try to go to bed earlier, but I notice I sleep less now that I'm in my 50s. Is that normal?",
  "About that nightly wine: It's still good for my brain, right?",
  "What else can I do to protect my brain?",
  "Why do I get sick?",
  "How do the disease-causing germs invade my body?",
  "What does the immune system do?",
  "What is the difference between bacteria and viruses?",
  "Do our intestines contain germs?",
  "Where are some other places that germs hide?",
  "What are antibiotics?",
  "How was penicillin developed?",
  "Can chicken soup help a cold go away?",
  "Why is exercise important to health?",
  "Why is sleep necessary for good health?",
  "How does substance abuse affect a person's health?",
  "Can alcohol be bad for your health?",
  "Why is smoking cigarettes unhealthy?",
  "Is secondhand smoke bad?"
])]